import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import {
  getActiveAIModelSetups,
  generateAIContent,
} from "../../services/aiGenerateService";
import { handleErrors } from "../../utils/errorHandler";
import {
  getModelSetupLabel,
  getSetupId,
  findDefaultSetupId,
} from "./AIUtils";
import {
  validateAIRequiredFields,
  buildDynamicFieldsFromRules,
} from "./validateAIRequiredFields";
import { parseAIResponse, getAIResponseData } from "./AIResponseParser";
import { buildInjectionPlan, injectAIFields } from "./AIFieldInjector";
import "./ai-generator.css";

/**
 * Compact AI assistant card (reference UX): model dropdown + Generate.
 * Placed inside form layout (e.g. Contents sidebar), not a global modal.
 */
const AiGenerate = ({
  pageLink,
  formData,
  setFormData,
  requiredFields = [],
  fieldMap = {},
  editorFields = [],
  editorHandlers = {},
  onAfterInject,
  title = "AI Assistant",
}) => {
  const [expanded, setExpanded] = useState(false);
  const [modelSetups, setModelSetups] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [selectedSetupId, setSelectedSetupId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  const safePageLink = useMemo(
    () => (pageLink || "").trim().replace(/^\//, ""),
    [pageLink]
  );

  const { valid: requiredValid, errorMsg: requiredErrorMsg } = useMemo(
    () => validateAIRequiredFields(requiredFields, formData),
    [requiredFields, formData]
  );

  useEffect(() => {
    let cancelled = false;

    const loadModels = async () => {
      setModelsLoading(true);
      try {
        const setups = await getActiveAIModelSetups();
        if (cancelled) return;
        const list = Array.isArray(setups) ? setups : [];
        setModelSetups(list);
        setSelectedSetupId((prev) => prev || findDefaultSetupId(list));
      } catch (error) {
        if (!cancelled) handleErrors(error);
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    };

    loadModels();
    return () => {
      cancelled = true;
    };
  }, []);

  const canGenerate =
    requiredValid && Boolean(selectedSetupId) && !generating && !modelsLoading;

  const handleGenerate = useCallback(async () => {
    if (!requiredValid) {
      toast.warning(requiredErrorMsg);
      return;
    }
    if (!selectedSetupId) {
      toast.warning("Please select an AI model.");
      return;
    }
    if (!safePageLink) {
      toast.error("Page link is missing for AI generation.");
      return;
    }

    setGenerating(true);
    try {
      const dynamicFields = buildDynamicFieldsFromRules(
        requiredFields,
        formData
      );

      const response = await generateAIContent({
        pageLink: safePageLink,
        tokenSetupId: Number(selectedSetupId),
        dynamicFields,
      });

      if (response?.data?.isSuccess === false) {
        toast.error(
          response.data.message || "AI generation failed. Please try again."
        );
        return;
      }

      const aiResponseData = getAIResponseData(response);
      if (!aiResponseData) {
        toast.error("Invalid AI response. Please try again.");
        return;
      }

      const { success, data, error } = parseAIResponse(aiResponseData);

      if (!success || !data) {
        toast.error(error || "Invalid AI response. Please try again.");
        return;
      }

      const plan = buildInjectionPlan(data, {
        fieldMap,
        editorFields,
      });

      if (!plan.length) {
        toast.error("AI response did not contain mappable fields.");
        return;
      }

      const injectedKeys = injectAIFields({
        setFormData,
        plan,
        editorHandlers,
      });

      setHasGenerated(true);
      toast.success("Content generated successfully!");

      if (onAfterInject) {
        onAfterInject(formData, injectedKeys, data);
      }
    } catch (error) {
      handleErrors(error);
      toast.error("AI generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  }, [
    requiredValid,
    requiredErrorMsg,
    selectedSetupId,
    safePageLink,
    requiredFields,
    formData,
    fieldMap,
    editorFields,
    editorHandlers,
    setFormData,
    onAfterInject,
  ]);

  const toggleExpanded = () => {
    if (!requiredValid && !expanded) {
      toast.warning(requiredErrorMsg);
      return;
    }
    setExpanded((prev) => !prev);
  };

  const assistantState = generating
    ? "loading"
    : hasGenerated
    ? "generated"
    : requiredValid
    ? "active"
    : "disabled";

  return (
    <div className={`ai-generate-wrap ai-generate-wrap--${assistantState}`}>
      {!expanded ? (
        <button
          type="button"
          className={`ai-generate-trigger ${!requiredValid ? "ai-generate-trigger--disabled" : ""} ${generating ? "ai-generate-trigger--loading" : ""}`}
          onClick={toggleExpanded}
          disabled={generating}
          title={requiredValid ? title : requiredErrorMsg}
          aria-label={title}
          aria-busy={generating}
        >
          <span className="ai-generate-trigger__glow" aria-hidden="true" />
          {generating ? (
            <span
              className="spinner-border spinner-border-sm ai-generate-trigger__icon"
              role="status"
              aria-hidden="true"
            />
          ) : (
            <i className="ri-magic-line ai-generate-trigger__icon" aria-hidden="true" />
          )}
          <span className="ai-generate-trigger__label">
            {generating ? "Generating…" : hasGenerated ? "Regenerate" : "Auto Generate With AI"}
          </span>
        </button>
      ) : (
        <div className="ai-generate-card">
          <div className="ai-generate-card__header">
            <div className="ai-generate-card__title">
              <i className="ri-magic-line" />
              <span>{title}</span>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-ghost-secondary ai-generate-card__close"
              onClick={() => setExpanded(false)}
              aria-label="Close AI assistant"
            >
              <i className="ri-close-line" />
            </button>
          </div>

          {!requiredValid && (
            <div className="ai-generate-card__warning" role="alert">
              <i className="ri-error-warning-line me-1" />
              {requiredErrorMsg}
            </div>
          )}

          <div className="ai-generate-card__body">
            <label htmlFor="ai-model-setup-select" className="form-label">
              AI Model
            </label>
            <select
              id="ai-model-setup-select"
              className="form-select form-select-sm"
              value={selectedSetupId}
              onChange={(e) => setSelectedSetupId(e.target.value)}
              disabled={modelsLoading || generating || !requiredValid}
            >
              <option value="">
                {modelsLoading ? "Loading models…" : "Select model"}
              </option>
              {modelSetups.map((setup) => {
                const id = getSetupId(setup);
                return (
                  <option key={id} value={String(id)}>
                    {getModelSetupLabel(setup)}
                  </option>
                );
              })}
            </select>

            <button
              type="button"
              className="btn btn-secondary btn-sm w-100 mt-3 ai-generate-card__submit"
              onClick={handleGenerate}
              disabled={!canGenerate}
            >
              {generating ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Generating…
                </>
              ) : hasGenerated ? (
                <>
                  <i className="ri-refresh-line me-1" />
                  Regenerate
                </>
              ) : (
                <>
                  <i className="ri-magic-line me-1" />
                  Generate
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiGenerate;
